<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import reportsAPI from 'dashboard/api/reports';
import { useMapGetter } from 'dashboard/composables/store';
import {
  downloadCsvFile,
  generateFileName,
} from 'dashboard/helper/downloadHelper';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import BarChart from 'shared/components/charts/BarChart.vue';
import LineChart from 'shared/components/charts/LineChart.vue';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';

const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledOnAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const isTeamsEnabled = computed(() =>
  isFeatureEnabledOnAccount.value?.(
    accountId.value,
    FEATURE_FLAGS.TEAM_MANAGEMENT
  )
);

const isLoading = ref(false);
const errorMessage = ref('');
const generatedAt = ref(null);
const isAvailabilityBreakdownOpen = ref(true);
const isStatusBreakdownOpen = ref(true);

const filters = ref({
  from: 0,
  to: 0,
  userIds: [],
  inboxId: null,
  teamId: null,
});

const summary = ref({
  current_period: {},
  previous_period: {},
  availability: {
    current_online_seconds: 0,
    previous_online_seconds: 0,
  },
  chats_by_status: {
    current: {
      total_count: 0,
      online_count: 0,
      offline_count: 0,
      online_percentage: 0,
      offline_percentage: 0,
    },
    previous: {
      total_count: 0,
      online_count: 0,
      offline_count: 0,
      online_percentage: 0,
      offline_percentage: 0,
    },
  },
});

const series = ref([]);

const toCsvValue = value => {
  const stringValue = String(value ?? '');
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const rowsToCsv = (headers, rows) => {
  const csvRows = [headers, ...rows];
  return csvRows
    .map(row => row.map(column => toCsvValue(column)).join(','))
    .join('\n');
};

const formatCount = value => Number(value || 0).toLocaleString();
const formatPercent = value => `${Number(value || 0).toFixed(2)}%`;

const formatDuration = totalSeconds => {
  const seconds = Number(totalSeconds || 0);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}min`;
};

const formatDurationDiff = seconds => {
  const numericValue = Number(seconds || 0);
  const sign = numericValue > 0 ? '+' : '';
  return `${sign}${formatDuration(Math.abs(numericValue))}`;
};

const currentPeriodLabel = computed(() => {
  const period = summary.value.current_period || {};
  if (!period.from || !period.to) return '-';
  return `${format(fromUnixTime(period.from), 'dd MMM yy')} - ${format(
    fromUnixTime(period.to),
    'dd MMM yy'
  )}`;
});

const previousPeriodLabel = computed(() => {
  const period = summary.value.previous_period || {};
  if (!period.from || !period.to) return '-';
  return `${format(fromUnixTime(period.from), 'dd MMM yy')} - ${format(
    fromUnixTime(period.to),
    'dd MMM yy'
  )}`;
});

const currentAvailabilitySeconds = computed(() =>
  Number(summary.value.availability?.current_online_seconds || 0)
);
const previousAvailabilitySeconds = computed(() =>
  Number(summary.value.availability?.previous_online_seconds || 0)
);

const currentStatusSummary = computed(
  () => summary.value.chats_by_status?.current || {}
);
const previousStatusSummary = computed(
  () => summary.value.chats_by_status?.previous || {}
);

const diffClass = value => {
  if (value > 0) return 'text-n-teal-10';
  if (value < 0) return 'text-n-ruby-9';
  return 'text-n-slate-11';
};

const availabilityRows = computed(() =>
  (series.value || []).map(row => {
    const currentDateLabel = format(
      fromUnixTime(row.current_timestamp),
      'dd MMM yy'
    );
    const previousDateLabel = format(
      fromUnixTime(row.previous_timestamp),
      'dd MMM yy'
    );

    return {
      ...row,
      currentDateLabel,
      previousDateLabel,
      availabilityDiffSeconds:
        Number(row.current_online_seconds || 0) -
        Number(row.previous_online_seconds || 0),
      totalDiff:
        Number(row.current_total_chats || 0) -
        Number(row.previous_total_chats || 0),
      onlineDiff:
        Number(row.current_online_chats || 0) -
        Number(row.previous_online_chats || 0),
      offlineDiff:
        Number(row.current_offline_chats || 0) -
        Number(row.previous_offline_chats || 0),
    };
  })
);

const availabilityChartCollection = computed(() => ({
  labels: availabilityRows.value.map(row =>
    format(fromUnixTime(row.current_timestamp), 'dd MMM')
  ),
  datasets: [
    {
      label: currentPeriodLabel.value,
      data: availabilityRows.value.map(row =>
        Number(row.current_online_seconds || 0)
      ),
      backgroundColor: '#1D75E5',
      borderRadius: 4,
      categoryPercentage: 0.68,
      barPercentage: 0.9,
    },
    {
      label: previousPeriodLabel.value,
      data: availabilityRows.value.map(row =>
        Number(row.previous_online_seconds || 0)
      ),
      backgroundColor: '#E7C51B',
      borderRadius: 4,
      categoryPercentage: 0.68,
      barPercentage: 0.9,
    },
  ],
}));

const availabilityChartOptions = {
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: value => `${Math.round(Number(value) / 3600)} h`,
      },
    },
  },
};

const statusChartCollection = computed(() => ({
  labels: availabilityRows.value.map(row =>
    format(fromUnixTime(row.current_timestamp), 'dd MMM')
  ),
  datasets: [
    {
      label: `${t('CHAT_AVAILABILITY_REPORT.ONLINE')} (${currentPeriodLabel.value})`,
      data: availabilityRows.value.map(row =>
        Number(row.current_online_chats || 0)
      ),
      borderColor: '#22C55E',
      backgroundColor: '#22C55E',
      pointStyle: 'rect',
      pointRadius: 3,
      tension: 0.2,
    },
    {
      label: `${t('CHAT_AVAILABILITY_REPORT.ONLINE')} (${previousPeriodLabel.value})`,
      data: availabilityRows.value.map(row =>
        Number(row.previous_online_chats || 0)
      ),
      borderColor: '#86EFAC',
      backgroundColor: '#86EFAC',
      pointStyle: 'triangle',
      pointRadius: 3,
      tension: 0.2,
    },
    {
      label: `${t('CHAT_AVAILABILITY_REPORT.OFFLINE')} (${currentPeriodLabel.value})`,
      data: availabilityRows.value.map(row =>
        Number(row.current_offline_chats || 0)
      ),
      borderColor: '#EF4444',
      backgroundColor: '#EF4444',
      pointStyle: 'rectRounded',
      pointRadius: 3,
      tension: 0.2,
    },
    {
      label: `${t('CHAT_AVAILABILITY_REPORT.OFFLINE')} (${previousPeriodLabel.value})`,
      data: availabilityRows.value.map(row =>
        Number(row.previous_offline_chats || 0)
      ),
      borderColor: '#FCA5A5',
      backgroundColor: '#FCA5A5',
      pointStyle: 'circle',
      pointRadius: 3,
      tension: 0.2,
    },
  ],
}));

const statusChartOptions = {
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: value => Number(value).toLocaleString(),
      },
    },
  },
};

const fetchReport = async () => {
  if (!filters.value.from || !filters.value.to) return;

  isLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await reportsAPI.getChatAvailability({
      from: filters.value.from,
      to: filters.value.to,
      userIds: filters.value.userIds,
      inboxId: filters.value.inboxId,
      teamId: filters.value.teamId,
    });

    summary.value = response.data?.summary || summary.value;
    series.value = response.data?.series || [];
    generatedAt.value = new Date();
  } catch {
    errorMessage.value = t('CHAT_AVAILABILITY_REPORT.FETCH_ERROR');
    series.value = [];
  } finally {
    isLoading.value = false;
  }
};

const onFilterChange = ({
  from,
  to,
  selectedAgents,
  selectedInbox,
  selectedTeam,
}) => {
  filters.value = {
    from,
    to,
    userIds: selectedAgents.map(agent => agent.id),
    inboxId: selectedInbox?.id,
    teamId: selectedTeam?.id,
  };
  fetchReport();
};

const downloadAvailabilityCsv = () => {
  const headers = [
    t('CHAT_AVAILABILITY_REPORT.SERIES'),
    ...availabilityRows.value.map(
      row =>
        `${row.currentDateLabel} ${t('CHAT_AVAILABILITY_REPORT.VS')} ${row.previousDateLabel}`
    ),
  ];

  const row = [
    t('CHAT_AVAILABILITY_REPORT.AVAILABILITY'),
    ...availabilityRows.value.map(
      item =>
        `${formatDuration(item.current_online_seconds)} | ${formatDuration(
          item.previous_online_seconds
        )} | ${formatDurationDiff(item.availabilityDiffSeconds)}`
    ),
  ];

  const csvContent = rowsToCsv(headers, [row]);
  const fileName = generateFileName({
    type: 'chat-availability-breakdown',
    to: filters.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const downloadStatusCsv = () => {
  const headers = [
    t('CHAT_AVAILABILITY_REPORT.SERIES'),
    ...availabilityRows.value.map(
      row =>
        `${row.currentDateLabel} ${t('CHAT_AVAILABILITY_REPORT.VS')} ${row.previousDateLabel}`
    ),
  ];

  const rows = [
    [
      t('CHAT_AVAILABILITY_REPORT.TOTAL'),
      ...availabilityRows.value.map(
        row =>
          `${formatCount(row.current_total_chats)} | ${formatCount(
            row.previous_total_chats
          )} | ${row.totalDiff > 0 ? '+' : ''}${formatCount(row.totalDiff)}`
      ),
    ],
    [
      t('CHAT_AVAILABILITY_REPORT.ONLINE'),
      ...availabilityRows.value.map(
        row =>
          `${formatCount(row.current_online_chats)} | ${formatCount(
            row.previous_online_chats
          )} | ${row.onlineDiff > 0 ? '+' : ''}${formatCount(row.onlineDiff)}`
      ),
    ],
    [
      t('CHAT_AVAILABILITY_REPORT.OFFLINE'),
      ...availabilityRows.value.map(
        row =>
          `${formatCount(row.current_offline_chats)} | ${formatCount(
            row.previous_offline_chats
          )} | ${row.offlineDiff > 0 ? '+' : ''}${formatCount(row.offlineDiff)}`
      ),
    ],
  ];

  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'chat-availability-status-breakdown',
    to: filters.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const generatedAtLabel = computed(() =>
  generatedAt.value
    ? generatedAt.value.toLocaleString()
    : t('LAST_7_DAYS_REPORT.NOT_GENERATED_YET')
);
</script>

<template>
  <div class="ca-page">
    <div class="ca-shell">
      <div class="ca-header">
        <div class="ca-header-left">
          <h1 class="ca-title">{{ t('CHAT_AVAILABILITY_REPORT.TITLE') }}</h1>
        </div>
      </div>

      <div class="ca-toolbar">
        <CompactDateInboxFilters
          :show-add-filter="false"
          :show-inbox="true"
          :show-agents="true"
          :show-teams="isTeamsEnabled"
          livechat-only-inboxes
          @filter-change="onFilterChange"
        />
      </div>

      <div class="ca-content">
        <div v-if="errorMessage" class="ca-error">
          {{ errorMessage }}
        </div>

        <section class="ca-card">
          <div class="ca-card-header">
            <div class="ca-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <h2 class="ca-card-title">
                {{ t('CHAT_AVAILABILITY_REPORT.TITLE') }}
              </h2>
              <span class="ca-period">{{ currentPeriodLabel }}</span>
            </div>
          </div>

          <div class="ca-section ca-section--no-bottom">
            <div class="ca-summary-grid ca-summary-grid--two">
              <div class="ca-summary-card">
                <p class="ca-summary-label">{{ currentPeriodLabel }}</p>
                <div class="ca-summary-value">
                  {{ formatDuration(currentAvailabilitySeconds) }}
                </div>
              </div>
              <div class="ca-summary-card">
                <p class="ca-summary-label">{{ previousPeriodLabel }}</p>
                <div class="ca-summary-value">
                  {{ formatDuration(previousAvailabilitySeconds) }}
                </div>
              </div>
            </div>

            <div class="ca-chart">
              <woot-loading-state
                v-if="isLoading"
                :message="$t('REPORT.LOADING_CHART')"
                class="h-full"
              />
              <div v-else-if="!availabilityRows.length" class="ca-empty">
                {{ t('CHAT_AVAILABILITY_REPORT.NO_DATA') }}
              </div>
              <BarChart
                v-else
                :collection="availabilityChartCollection"
                :chart-options="availabilityChartOptions"
              />
            </div>
          </div>

          <div class="ca-breakdown">
            <div
              class="ca-breakdown-header"
              role="button"
              tabindex="0"
              @click="
                isAvailabilityBreakdownOpen = !isAvailabilityBreakdownOpen
              "
              @keydown.enter.prevent="
                isAvailabilityBreakdownOpen = !isAvailabilityBreakdownOpen
              "
              @keydown.space.prevent="
                isAvailabilityBreakdownOpen = !isAvailabilityBreakdownOpen
              "
            >
              <div class="ca-breakdown-title">
                <svg
                  class="ca-breakdown-caret"
                  :class="{ 'is-collapsed': !isAvailabilityBreakdownOpen }"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m6 9 6 6 6-6"
                  />
                </svg>
                <span>{{ t('CHAT_AVAILABILITY_REPORT.BREAKDOWN_TITLE') }}</span>
              </div>

              <button
                class="ca-export-btn"
                type="button"
                @click.stop="downloadAvailabilityCsv"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                  />
                </svg>
                <span>{{ t('CHAT_AVAILABILITY_REPORT.EXPORT_CSV') }}</span>
              </button>
            </div>

            <div v-show="isAvailabilityBreakdownOpen" class="ca-table-wrap">
              <table class="ca-table">
                <thead>
                  <tr>
                    <th class="ca-th-series">
                      {{ t('CHAT_AVAILABILITY_REPORT.SERIES') }}
                    </th>
                    <th
                      v-for="row in availabilityRows"
                      :key="`availability-header-${row.current_timestamp}`"
                      class="ca-th-data"
                    >
                      <div>{{ row.currentDateLabel }}</div>
                      <div class="ca-th-sub">
                        {{ t('CHAT_AVAILABILITY_REPORT.VS') }}
                        {{ row.previousDateLabel }}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="ca-td-series">
                      {{ t('CHAT_AVAILABILITY_REPORT.AVAILABILITY') }}
                    </td>
                    <td
                      v-for="row in availabilityRows"
                      :key="`availability-row-${row.current_timestamp}`"
                      class="ca-td-data"
                    >
                      <div class="ca-cell-main">
                        {{ formatDuration(row.current_online_seconds) }}
                      </div>
                      <div class="ca-cell-sub">
                        {{ formatDuration(row.previous_online_seconds) }}
                      </div>
                      <div
                        class="ca-cell-diff"
                        :class="diffClass(row.availabilityDiffSeconds)"
                      >
                        {{ formatDurationDiff(row.availabilityDiffSeconds) }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section class="ca-card">
          <div class="ca-card-header">
            <div class="ca-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <h2 class="ca-card-title">
                {{ t('CHAT_AVAILABILITY_REPORT.STATUS_SECTION_TITLE') }}
              </h2>
              <span class="ca-period">{{ currentPeriodLabel }}</span>
            </div>
          </div>

          <div class="ca-section ca-section--no-bottom">
            <div class="ca-summary-grid">
              <div class="ca-summary-card">
                <p class="ca-summary-label">
                  {{ t('CHAT_AVAILABILITY_REPORT.ONLINE') }}
                </p>
                <p class="ca-summary-sub">{{ currentPeriodLabel }}</p>
                <div class="ca-summary-value">
                  {{ formatCount(currentStatusSummary.online_count) }}
                </div>
                <div class="ca-summary-note">
                  {{ formatPercent(currentStatusSummary.online_percentage) }}
                </div>
              </div>
              <div class="ca-summary-card">
                <p class="ca-summary-label">
                  {{ t('CHAT_AVAILABILITY_REPORT.ONLINE') }}
                </p>
                <p class="ca-summary-sub">{{ previousPeriodLabel }}</p>
                <div class="ca-summary-value">
                  {{ formatCount(previousStatusSummary.online_count) }}
                </div>
                <div class="ca-summary-note">
                  {{ formatPercent(previousStatusSummary.online_percentage) }}
                </div>
              </div>
              <div class="ca-summary-card">
                <p class="ca-summary-label">
                  {{ t('CHAT_AVAILABILITY_REPORT.OFFLINE') }}
                </p>
                <p class="ca-summary-sub">{{ currentPeriodLabel }}</p>
                <div class="ca-summary-value">
                  {{ formatCount(currentStatusSummary.offline_count) }}
                </div>
                <div class="ca-summary-note">
                  {{ formatPercent(currentStatusSummary.offline_percentage) }}
                </div>
              </div>
              <div class="ca-summary-card">
                <p class="ca-summary-label">
                  {{ t('CHAT_AVAILABILITY_REPORT.OFFLINE') }}
                </p>
                <p class="ca-summary-sub">{{ previousPeriodLabel }}</p>
                <div class="ca-summary-value">
                  {{ formatCount(previousStatusSummary.offline_count) }}
                </div>
                <div class="ca-summary-note">
                  {{ formatPercent(previousStatusSummary.offline_percentage) }}
                </div>
              </div>
            </div>

            <div class="ca-chart">
              <woot-loading-state
                v-if="isLoading"
                :message="$t('REPORT.LOADING_CHART')"
                class="h-full"
              />
              <div v-else-if="!availabilityRows.length" class="ca-empty">
                {{ t('CHAT_AVAILABILITY_REPORT.NO_DATA') }}
              </div>
              <LineChart
                v-else
                :collection="statusChartCollection"
                :chart-options="statusChartOptions"
              />
            </div>
          </div>

          <div class="ca-breakdown">
            <div
              class="ca-breakdown-header"
              role="button"
              tabindex="0"
              @click="isStatusBreakdownOpen = !isStatusBreakdownOpen"
              @keydown.enter.prevent="
                isStatusBreakdownOpen = !isStatusBreakdownOpen
              "
              @keydown.space.prevent="
                isStatusBreakdownOpen = !isStatusBreakdownOpen
              "
            >
              <div class="ca-breakdown-title">
                <svg
                  class="ca-breakdown-caret"
                  :class="{ 'is-collapsed': !isStatusBreakdownOpen }"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m6 9 6 6 6-6"
                  />
                </svg>
                <span>{{
                  t('CHAT_AVAILABILITY_REPORT.STATUS_BREAKDOWN_TITLE')
                }}</span>
              </div>

              <button
                class="ca-export-btn"
                type="button"
                @click.stop="downloadStatusCsv"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                  />
                </svg>
                <span>{{ t('CHAT_AVAILABILITY_REPORT.EXPORT_CSV') }}</span>
              </button>
            </div>

            <div v-show="isStatusBreakdownOpen" class="ca-table-wrap">
              <table class="ca-table">
                <thead>
                  <tr>
                    <th class="ca-th-series">
                      {{ t('CHAT_AVAILABILITY_REPORT.SERIES') }}
                    </th>
                    <th
                      v-for="row in availabilityRows"
                      :key="`status-header-${row.current_timestamp}`"
                      class="ca-th-data"
                    >
                      <div>{{ row.currentDateLabel }}</div>
                      <div class="ca-th-sub">
                        {{ t('CHAT_AVAILABILITY_REPORT.VS') }}
                        {{ row.previousDateLabel }}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="ca-td-series">
                      {{ t('CHAT_AVAILABILITY_REPORT.TOTAL') }}
                    </td>
                    <td
                      v-for="row in availabilityRows"
                      :key="`status-total-${row.current_timestamp}`"
                      class="ca-td-data"
                    >
                      <div class="ca-cell-main">
                        {{ formatCount(row.current_total_chats) }}
                      </div>
                      <div class="ca-cell-sub">
                        {{ formatCount(row.previous_total_chats) }}
                      </div>
                      <div
                        class="ca-cell-diff"
                        :class="diffClass(row.totalDiff)"
                      >
                        {{ row.totalDiff > 0 ? '+' : ''
                        }}{{ formatCount(row.totalDiff) }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="ca-td-series">
                      {{ t('CHAT_AVAILABILITY_REPORT.ONLINE') }}
                    </td>
                    <td
                      v-for="row in availabilityRows"
                      :key="`status-online-${row.current_timestamp}`"
                      class="ca-td-data"
                    >
                      <div class="ca-cell-main">
                        {{ formatCount(row.current_online_chats) }}
                      </div>
                      <div class="ca-cell-sub">
                        {{ formatCount(row.previous_online_chats) }}
                      </div>
                      <div
                        class="ca-cell-diff"
                        :class="diffClass(row.onlineDiff)"
                      >
                        {{ row.onlineDiff > 0 ? '+' : ''
                        }}{{ formatCount(row.onlineDiff) }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="ca-td-series">
                      {{ t('CHAT_AVAILABILITY_REPORT.OFFLINE') }}
                    </td>
                    <td
                      v-for="row in availabilityRows"
                      :key="`status-offline-${row.current_timestamp}`"
                      class="ca-td-data"
                    >
                      <div class="ca-cell-main">
                        {{ formatCount(row.current_offline_chats) }}
                      </div>
                      <div class="ca-cell-sub">
                        {{ formatCount(row.previous_offline_chats) }}
                      </div>
                      <div
                        class="ca-cell-diff"
                        :class="diffClass(row.offlineDiff)"
                      >
                        {{ row.offlineDiff > 0 ? '+' : ''
                        }}{{ formatCount(row.offlineDiff) }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <p class="ca-footer-note">
          {{
            t('CHAT_AVAILABILITY_REPORT.GENERATED_AT', {
              time: generatedAtLabel,
            })
          }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ca-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}
.ca-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

.ca-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
}
.ca-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}
.ca-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}
.ca-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 3px 10px -6px;
}

.ca-content {
  padding: 16px 32px;
}
.ca-error {
  margin-bottom: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

.ca-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #fff;
  box-sizing: border-box;
  margin-bottom: 20px;
  overflow: hidden;
}
.ca-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e5e7eb;
}
.ca-card-title-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.ca-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}
.ca-period {
  font-size: 12px;
  color: #6b7280;
  margin-left: 10px;
  white-space: nowrap;
}

.ca-section {
  padding: 16px 20px 20px;
}
.ca-section--no-bottom {
  padding-bottom: 0;
}

.ca-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}
.ca-summary-grid--two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
@media (max-width: 1100px) {
  .ca-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .ca-summary-grid--two {
    grid-template-columns: 1fr;
  }
}
.ca-summary-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
  padding: 12px;
}
.ca-summary-label {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #111827;
}
.ca-summary-sub {
  margin: 2px 0 0;
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
}
.ca-summary-value {
  margin-top: 10px;
  font-size: 20px;
  line-height: 28px;
  font-weight: 800;
  color: #111827;
}
.ca-summary-note {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
  font-weight: 700;
}

.ca-chart {
  height: 384px;
  margin-top: 16px;
}
.ca-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}

.ca-breakdown {
  border-top: 1px solid #dfe3e8;
}
.ca-breakdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 16px;
  cursor: pointer;
  user-select: none;
}
.ca-breakdown-title {
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.ca-breakdown-caret {
  transition: transform 0.18s ease;
}
.ca-breakdown-caret.is-collapsed {
  transform: rotate(-90deg);
}

.ca-export-btn {
  min-width: 128px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.ca-export-btn:hover {
  background: #f9fafb;
}

.ca-table-wrap {
  overflow-x: auto;
  border-top: 1px solid #dfe3e8;
}
.ca-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  color: #424d57;
}
.ca-th-series {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #ffffff;
  border-right: 1px solid #edf1f5;
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  color: #111827;
}
.ca-th-data {
  padding: 10px 12px;
  text-align: left;
  font-weight: 700;
  color: #111827;
  border-bottom: 1px solid #edf1f5;
  background: #f8fafc;
}
.ca-th-sub {
  margin-top: 2px;
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
}
.ca-td-series {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #ffffff;
  border-right: 1px solid #edf1f5;
  padding: 12px;
  font-weight: 600;
  color: #111827;
  border-bottom: 1px solid #edf1f5;
}
.ca-td-data {
  padding: 10px 12px;
  border-bottom: 1px solid #edf1f5;
  vertical-align: top;
}
.ca-cell-main {
  font-weight: 800;
  color: #111827;
}
.ca-cell-sub {
  margin-top: 2px;
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
}
.ca-cell-diff {
  margin-top: 2px;
  font-size: 11px;
  font-weight: 700;
}

.ca-footer-note {
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

::global(body.dark) .ca-page,
::global(body.dark) .ca-shell,
::global(body.dark) .ca-card,
::global(body.dark) .ca-header,
::global(body.dark) .ca-toolbar,
::global(body.dark) .ca-th-series,
::global(body.dark) .ca-th-data,
::global(body.dark) .ca-td-series,
::global(body.dark) .ca-td-data,
::global(body.theme-mix) .ca-page,
::global(body.theme-mix) .ca-shell,
::global(body.theme-mix) .ca-card,
::global(body.theme-mix) .ca-header,
::global(body.theme-mix) .ca-toolbar,
::global(body.theme-mix) .ca-th-series,
::global(body.theme-mix) .ca-th-data,
::global(body.theme-mix) .ca-td-series,
::global(body.theme-mix) .ca-td-data {
  background: #fff !important;
  color: #111827 !important;
}

::global(body.dark) .ca-th-series *,
::global(body.dark) .ca-th-data *,
::global(body.theme-mix) .ca-th-series *,
::global(body.theme-mix) .ca-th-data * {
  color: #111827 !important;
}
</style>
